import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import PayerCombobox from './PayerCombobox';
import { useT } from '../../i18n/LocaleContext';
import { useData } from '../../data/DataContext';
import { formatDateInput } from '../../utils/format';

const TYPES = ['cotisation', 'pelerinage', 'offrande'];
const METHODS = ['mobile_money', 'bank_transfer', 'cash', 'card'];
const STATUSES = ['paid', 'pending', 'cancelled', 'refunded'];

const emptyForm = {
  memberId: '',
  payerName: '',
  type: 'cotisation',
  amount: '',
  date: '',
  method: 'mobile_money',
  eventId: '',
  status: 'paid',
  notes: '',
};

export default function PaymentFormModal({ isOpen, onClose, onSubmit, initial }) {
  const t = useT();
  const { members, events } = useData();
  const isEdit = Boolean(initial);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (initial) {
      setForm({
        memberId: initial.memberId || '',
        payerName: initial.payerName || '',
        type: initial.type || 'cotisation',
        amount: initial.amount ?? '',
        date: formatDateInput(initial.date) || formatDateInput(new Date()),
        method: initial.method || 'mobile_money',
        eventId: initial.eventId || '',
        status: initial.status || 'paid',
        notes: initial.notes || '',
      });
    } else {
      setForm({ ...emptyForm, date: formatDateInput(new Date()) });
    }
    setErrors({});
  }, [isOpen, initial]);

  const set = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'type' && value !== 'pelerinage') next.eventId = '';
      return next;
    });
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handlePayerChange = ({ memberId, payerName }) => {
    setForm((prev) => ({ ...prev, memberId: memberId || '', payerName: payerName || '' }));
    if (errors.payer) setErrors((prev) => ({ ...prev, payer: undefined }));
  };

  const typeOptions = useMemo(() => TYPES.map((v) => ({ value: v, label: t(`payments.types.${v}`) })), [t]);
  const methodOptions = useMemo(() => METHODS.map((v) => ({ value: v, label: t(`payments.methods.${v}`) })), [t]);
  const statusOptions = useMemo(() => STATUSES.map((v) => ({ value: v, label: t(`payments.statuses.${v}`) })), [t]);
  const eventOptions = useMemo(
    () => events
      .filter((e) => e.status !== 'cancelled')
      .map((e) => ({ value: e.id, label: `${e.name} — ${e.code || e.id}` })),
    [events]
  );

  const validate = () => {
    const next = {};
    if (!form.memberId && !form.payerName.trim()) next.payer = t('payments.payer_required');
    const amount = Number(form.amount);
    if (form.amount === '' || Number.isNaN(amount) || amount <= 0) next.amount = t('validation.number_required');
    if (!form.date) next.date = t('validation.required');
    if (form.type === 'pelerinage' && !form.eventId) next.eventId = t('validation.required');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        memberId: form.memberId || null,
        payerName: form.memberId ? '' : form.payerName.trim(),
        type: form.type,
        amount: Number(form.amount),
        date: form.date,
        method: form.method,
        eventId: form.eventId || null,
        status: form.status,
        notes: form.notes,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      title={isEdit ? t('payments.edit_payment') : t('payments.new_payment')}
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>{t('common.cancel')}</Button>
          <Button variant="gold" onClick={handleSubmit} loading={submitting}>
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </>
      )}
    >
      <form onSubmit={handleSubmit} noValidate>
        <FormField
          label={t('payments.field_payer')}
          htmlFor="payer"
          required
          error={errors.payer}
          hint={!errors.payer ? t('payments.payer_hint') : undefined}
        >
          <PayerCombobox
            id="payer"
            members={members}
            value={{ memberId: form.memberId, payerName: form.payerName }}
            onChange={handlePayerChange}
            invalid={Boolean(errors.payer)}
            placeholder={t('payments.payer_hint')}
            autoFocus={!isEdit}
          />
        </FormField>
        <div className="form-row form-row--2">
          <FormField label={t('payments.field_type')} htmlFor="type">
            <Select id="type" value={form.type} onChange={set('type')} options={typeOptions} />
          </FormField>
          <FormField label={t('payments.field_amount')} htmlFor="amount" required error={errors.amount}>
            <Input id="amount" type="number" min="0" value={form.amount} onChange={set('amount')} invalid={Boolean(errors.amount)} />
          </FormField>
        </div>
        {form.type === 'pelerinage' && (
          <FormField label={t('payments.field_event')} htmlFor="eventId" required error={errors.eventId}>
            <Select
              id="eventId"
              value={form.eventId}
              onChange={set('eventId')}
              options={eventOptions}
              placeholder={t('common.select_placeholder')}
              invalid={Boolean(errors.eventId)}
            />
          </FormField>
        )}
        <div className="form-row form-row--3">
          <FormField label={t('payments.field_date')} htmlFor="date" required error={errors.date}>
            <Input id="date" type="date" value={form.date} onChange={set('date')} invalid={Boolean(errors.date)} />
          </FormField>
          <FormField label={t('payments.field_method')} htmlFor="method">
            <Select id="method" value={form.method} onChange={set('method')} options={methodOptions} />
          </FormField>
          <FormField label={t('payments.field_status')} htmlFor="status">
            <Select id="status" value={form.status} onChange={set('status')} options={statusOptions} />
          </FormField>
        </div>
        <FormField label={t('payments.field_notes')} htmlFor="notes" hint={t('common.optional')}>
          <Textarea id="notes" value={form.notes} onChange={set('notes')} />
        </FormField>
      </form>
    </Modal>
  );
}
